(function (global) {
  "use strict";

  /*
   * LT Liquid Glass v1.0.0
   * Reusable soft-body motion for buttons, tabs, and slider indicators.
   *
   * The motion driver writes only --lt-liquid-* custom properties from
   * assets/lt-liquid-glass.css. At rest every inline value is removed so the
   * approved maximum-prominence material remains the source of truth.
   */

  const VERSION = "1.0.0";
  if (global.LTLiquidGlass?.version === VERSION) return;
  const PROPERTY_NAMES = [
    "--lt-liquid-angle",
    "--lt-liquid-angle-inverse",
    "--lt-liquid-scale-x",
    "--lt-liquid-scale-y",
    "--lt-liquid-lag-x",
    "--lt-liquid-lag-y",
    "--lt-liquid-radius",
    "--lt-liquid-glass-leading-x",
    "--lt-liquid-glass-leading-y",
    "--lt-liquid-glass-trailing-x",
    "--lt-liquid-glass-trailing-y",
    "--lt-liquid-glass-rim-opacity",
    "--lt-liquid-glass-lens-opacity",
    "--lt-liquid-glass-brightness",
    "--lt-liquid-glass-saturation",
    "--lt-liquid-glass-contrast"
  ];

  const reducedMotionQuery =
    typeof global.matchMedia === "function"
      ? global.matchMedia("(prefers-reduced-motion: reduce)")
      : { matches: false };
  const followRegistry = new WeakMap();
  const activeFollowers = new Set();

  function clamp(value, minimum, maximum) {
    return Math.max(minimum, Math.min(maximum, value));
  }

  function setProperty(host, name, value) {
    host.style.setProperty(name, value);
  }

  function clearProperties(host) {
    PROPERTY_NAMES.forEach((name) => host.style.removeProperty(name));
  }

  function readProfile(host) {
    const styles = getComputedStyle(host);
    const read = function (name, fallback) {
      const value = parseFloat(styles.getPropertyValue(name));
      return Number.isFinite(value) ? value : fallback;
    };

    return {
      rimRest: read("--lt-lg-rim-rest", 0.78),
      rimPeak: read("--lt-lg-rim-peak", 0.96),
      lensRest: read("--lt-lg-lens-rest", 0.14),
      lensPeak: read("--lt-lg-lens-peak", 0.26),
      brightnessRest: read("--lt-lg-brightness-rest", 103),
      brightnessPeak: read("--lt-lg-brightness-peak", 106),
      saturationRest: read("--lt-lg-saturation-rest", 110),
      saturationPeak: read("--lt-lg-saturation-peak", 112),
      contrastRest: read("--lt-lg-contrast-rest", 107),
      contrastPeak: read("--lt-lg-contrast-peak", 110)
    };
  }

  function getRestSnapshot(host, profile) {
    const height = host.offsetHeight || 44;
    const width = host.offsetWidth || 88;

    return {
      angle: 0,
      scaleX: 1,
      scaleY: 1,
      lagX: 0,
      lagY: 0,
      radius: height / 2,
      radiusText: "999px",
      leadingX: 50,
      leadingY: 24,
      trailingX: 50,
      trailingY: 76,
      rimOpacity: profile.rimRest,
      lensOpacity: profile.lensRest,
      brightness: profile.brightnessRest,
      saturation: profile.saturationRest,
      contrast: profile.contrastRest,
      opticalStrength: 0,
      directionX: 1,
      directionY: 0,
      shape: 0,
      shapeVelocity: 0,
      width: width,
      height: height,
      settled: true
    };
  }

  function createMotion(host, options) {
    const settings = options || {};
    const surface =
      settings.surface || host.querySelector(".lt-liquid-glass__surface");
    const motionQuery = settings.reducedMotionQuery || reducedMotionQuery;
    const profile = readProfile(host);

    let velocityX = 0;
    let velocityY = 0;
    let travelAngle = 0;
    let visualDirectionX = 1;
    let visualDirectionY = 0;
    let shape = 0;
    let shapeVelocity = 0;
    let releasingShape = false;
    let lastSnapshot = getRestSnapshot(host, profile);

    const velocityFilterRate = settings.velocityFilterRate || 18;
    const speedReference = settings.speedReference || 900;
    const shapeStiffness = settings.shapeStiffness || 145;
    const shapeDamping = settings.shapeDamping || 10;

    function render() {
      if (!surface || motionQuery.matches) {
        clearProperties(host);
        lastSnapshot = getRestSnapshot(host, profile);
        return lastSnapshot;
      }

      const positiveShape = clamp(shape, 0, 1.08);
      const recoilShape = clamp(-shape, 0, 0.22);
      const magnitude = clamp(positiveShape + recoilShape * 0.7, 0, 1);
      const directionX = visualDirectionX;
      const directionY = visualDirectionY;
      const velocityStrength = clamp(Math.abs(shapeVelocity) / 6.5, 0, 1);
      const scaleX = clamp(
        1 + positiveShape * 0.32 - recoilShape * 0.2,
        0.94,
        1.35
      );
      const scaleY = clamp(
        Math.pow(scaleX, -0.5) + recoilShape * 0.07,
        0.84,
        1.1
      );
      const lagDistance = positiveShape * 3.2;
      const height = host.offsetHeight || 44;
      const width = host.offsetWidth || 88;
      const leadingRadius = height / 2;
      const opticalStrength = clamp(
        magnitude * 0.78 + velocityStrength * 0.32,
        0,
        1
      );
      const movingLeadingX = 50 + directionX * 39;
      const movingLeadingY = 50 + directionY * 39;
      const movingTrailingX = 50 - directionX * 39;
      const movingTrailingY = 50 - directionY * 39;
      const leadingX = 50 + (movingLeadingX - 50) * opticalStrength;
      const leadingY = 24 + (movingLeadingY - 24) * opticalStrength;
      const trailingX = 50 + (movingTrailingX - 50) * opticalStrength;
      const trailingY = 76 + (movingTrailingY - 76) * opticalStrength;
      let radiusText = `${leadingRadius.toFixed(2)}px`;

      if (positiveShape > 0.008) {
        const radiusForCorner = (cornerX, cornerY) => {
          const directionProjection = clamp(
            cornerX * directionX + cornerY * directionY,
            -1,
            1
          );
          const radiusFactor =
            1 - positiveShape * 0.19 * (1 - directionProjection);
          return `${(leadingRadius * radiusFactor).toFixed(2)}px`;
        };

        radiusText = [
          radiusForCorner(-1, -1),
          radiusForCorner(1, -1),
          radiusForCorner(1, 1),
          radiusForCorner(-1, 1)
        ].join(" ");
      }

      const lagX = -directionX * lagDistance;
      const lagY = -directionY * lagDistance;
      const rimOpacity =
        profile.rimRest + (profile.rimPeak - profile.rimRest) * opticalStrength;
      const lensOpacity =
        profile.lensRest +
        (profile.lensPeak - profile.lensRest) * opticalStrength;
      const brightness =
        profile.brightnessRest +
        (profile.brightnessPeak - profile.brightnessRest) * opticalStrength;
      const saturation =
        profile.saturationRest +
        (profile.saturationPeak - profile.saturationRest) * opticalStrength;
      const contrast =
        profile.contrastRest +
        (profile.contrastPeak - profile.contrastRest) * opticalStrength;

      setProperty(host, "--lt-liquid-angle", `${travelAngle.toFixed(5)}rad`);
      setProperty(host, "--lt-liquid-angle-inverse", `${(-travelAngle).toFixed(5)}rad`);
      setProperty(host, "--lt-liquid-scale-x", scaleX.toFixed(4));
      setProperty(host, "--lt-liquid-scale-y", scaleY.toFixed(4));
      setProperty(host, "--lt-liquid-lag-x", `${lagX.toFixed(3)}px`);
      setProperty(host, "--lt-liquid-lag-y", `${lagY.toFixed(3)}px`);
      setProperty(host, "--lt-liquid-radius", radiusText);
      setProperty(host, "--lt-liquid-glass-leading-x", `${leadingX.toFixed(2)}%`);
      setProperty(host, "--lt-liquid-glass-leading-y", `${leadingY.toFixed(2)}%`);
      setProperty(host, "--lt-liquid-glass-trailing-x", `${trailingX.toFixed(2)}%`);
      setProperty(host, "--lt-liquid-glass-trailing-y", `${trailingY.toFixed(2)}%`);
      setProperty(host, "--lt-liquid-glass-rim-opacity", rimOpacity.toFixed(3));
      setProperty(host, "--lt-liquid-glass-lens-opacity", lensOpacity.toFixed(3));
      setProperty(host, "--lt-liquid-glass-brightness", `${brightness.toFixed(2)}%`);
      setProperty(host, "--lt-liquid-glass-saturation", `${saturation.toFixed(2)}%`);
      setProperty(host, "--lt-liquid-glass-contrast", `${contrast.toFixed(2)}%`);

      lastSnapshot = {
        angle: travelAngle,
        scaleX: scaleX,
        scaleY: scaleY,
        lagX: lagX,
        lagY: lagY,
        radius: leadingRadius,
        radiusText: radiusText,
        leadingX: leadingX,
        leadingY: leadingY,
        trailingX: trailingX,
        trailingY: trailingY,
        rimOpacity: rimOpacity,
        lensOpacity: lensOpacity,
        brightness: brightness,
        saturation: saturation,
        contrast: contrast,
        opticalStrength: opticalStrength,
        directionX: directionX,
        directionY: directionY,
        shape: shape,
        shapeVelocity: shapeVelocity,
        width: width,
        height: height,
        settled: false
      };

      return lastSnapshot;
    }

    function reset() {
      velocityX = 0;
      velocityY = 0;
      travelAngle = 0;
      visualDirectionX = 1;
      visualDirectionY = 0;
      shape = 0;
      shapeVelocity = 0;
      releasingShape = false;
      clearProperties(host);
      lastSnapshot = getRestSnapshot(host, profile);
      return lastSnapshot;
    }

    function update(frame) {
      const elapsedSeconds = clamp(
        Number(frame.elapsedSeconds) || 1 / 60,
        1 / 240,
        0.034
      );

      if (!surface || motionQuery.matches) {
        const snapshot = reset();
        return { settled: true, snapshot: snapshot };
      }

      const movedX = Number(frame.movedX) || 0;
      const movedY = Number(frame.movedY) || 0;
      const targetDistance = Math.max(0, Number(frame.targetDistance) || 0);
      const pointerIsIdle = Boolean(frame.pointerIsIdle);
      const velocityBlend = 1 - Math.exp(-velocityFilterRate * elapsedSeconds);
      const measuredVelocityX = movedX / Math.max(elapsedSeconds, 1 / 240);
      const measuredVelocityY = movedY / Math.max(elapsedSeconds, 1 / 240);

      velocityX += (measuredVelocityX - velocityX) * velocityBlend;
      velocityY += (measuredVelocityY - velocityY) * velocityBlend;

      const speed = Math.hypot(velocityX, velocityY);
      if (speed > 10) {
        const targetAngle = Math.atan2(velocityY, velocityX);
        const axisDelta =
          0.5 *
          Math.atan2(
            Math.sin(2 * (targetAngle - travelAngle)),
            Math.cos(2 * (targetAngle - travelAngle))
          );
        travelAngle += axisDelta * (1 - Math.exp(-14 * elapsedSeconds));

        const directionBlend = 1 - Math.exp(-9 * elapsedSeconds);
        visualDirectionX +=
          (velocityX / speed - visualDirectionX) * directionBlend;
        visualDirectionY +=
          (velocityY / speed - visualDirectionY) * directionBlend;
      }

      const speedInput = clamp(speed / speedReference, 0, 1);
      const pullInput = clamp(targetDistance / 260, 0, 1);
      const rawShapeTarget = Math.pow(
        clamp(speedInput * 0.72 + pullInput * 0.28, 0, 1),
        0.72
      );

      if (!releasingShape && pointerIsIdle && targetDistance < 40 && speed < 200) {
        releasingShape = true;
      } else if (releasingShape && !pointerIsIdle) {
        releasingShape = false;
      }

      const shapeTarget = releasingShape ? 0 : rawShapeTarget;
      const substeps = Math.max(1, Math.ceil(elapsedSeconds / (1 / 120)));
      const substepSeconds = elapsedSeconds / substeps;

      for (let step = 0; step < substeps; step += 1) {
        const acceleration =
          (shapeTarget - shape) * shapeStiffness - shapeVelocity * shapeDamping;
        shapeVelocity = clamp(
          shapeVelocity + acceleration * substepSeconds,
          -8,
          8
        );
        shape = clamp(shape + shapeVelocity * substepSeconds, -0.22, 1.08);
      }

      const snapshot = render();
      const settled =
        speed < 0.45 &&
        targetDistance < 0.25 &&
        Math.abs(shape) < 0.0015 &&
        Math.abs(shapeVelocity) < 0.008;

      snapshot.settled = settled;
      return { settled: settled, snapshot: snapshot };
    }

    return {
      update: update,
      render: render,
      reset: reset,
      getSnapshot: function () {
        return lastSnapshot;
      },
      destroy: reset
    };
  }

  function ensurePointerTracker() {
    if (global.__ltLiquidGlassPointerTrackerBound) return;
    global.__ltLiquidGlassPointerTrackerBound = true;
    global.__ltLiquidGlassPointer = { x: 0, y: 0, has: false };

    document.addEventListener(
      "pointermove",
      function (event) {
        global.__ltLiquidGlassPointer.x = event.clientX;
        global.__ltLiquidGlassPointer.y = event.clientY;
        global.__ltLiquidGlassPointer.has = true;
      },
      { passive: true }
    );
  }

  function getLastPointer() {
    ensurePointerTracker();
    const pointer = global.__ltLiquidGlassPointer;
    return pointer && pointer.has ? pointer : null;
  }

  function createPointerFollower(options) {
    const settings = options || {};
    const boundary = settings.boundary;
    const host = settings.host;
    const surface =
      settings.surface || host?.querySelector(".lt-liquid-glass__surface");
    const originalHostStyle = host
      ? {
          opacity: host.style.getPropertyValue("opacity"),
          pointerEvents: host.style.getPropertyValue("pointer-events"),
          transform: host.style.getPropertyValue("transform")
        }
      : null;

    if (!boundary || !host || !surface) {
      return {
        sync: function () {},
        deactivate: function () {},
        destroy: function () {}
      };
    }

    const registeredFollower = followRegistry.get(host);
    if (registeredFollower) return registeredFollower;

    ensurePointerTracker();

    const isActive =
      typeof settings.isActive === "function" ? settings.isActive : function () { return true; };
    const pointerProvider =
      typeof settings.getPointer === "function" ? settings.getPointer : getLastPointer;
    const onRender =
      typeof settings.onRender === "function" ? settings.onRender : function () {};
    const onVisibilityChange =
      typeof settings.onVisibilityChange === "function"
        ? settings.onVisibilityChange
        : function () {};
    const motion = createMotion(host, {
      surface: surface,
      reducedMotionQuery: settings.reducedMotionQuery || reducedMotionQuery
    });

    let rect = null;
    let targetX = 0;
    let targetY = 0;
    let currentX = 0;
    let currentY = 0;
    let frameRequest = null;
    let running = false;
    let shown = false;
    let lastFrameTime = 0;
    let lastTargetMoveTime = 0;
    let controller = null;
    let destroyed = false;

    const followRate = -Math.log(1 - 0.05) * 60;
    const maximumFrameSeconds = 0.034;
    const motionQuery = settings.reducedMotionQuery || reducedMotionQuery;

    function emit(snapshot) {
      onRender(
        Object.assign({}, snapshot, {
          centerX: currentX,
          centerY: currentY,
          visible: shown,
          boundary: boundary,
          host: host,
          surface: surface
        })
      );
    }

    function applyTransform(x, y) {
      host.style.transform =
        `translate3d(${x}px,${y}px,0) translate(-50%, -50%)`;
    }

    function setTarget(x, y, now) {
      if (Math.hypot(x - targetX, y - targetY) > 0.001) {
        lastTargetMoveTime = now || performance.now();
      }
      targetX = x;
      targetY = y;
    }

    function show() {
      if (shown) return;
      shown = true;
      host.style.opacity = "1";
      host.style.pointerEvents = "auto";
      onVisibilityChange(true);
    }

    function hide() {
      if (!shown) return;
      shown = false;
      host.style.opacity = "0";
      host.style.pointerEvents = "none";
      onVisibilityChange(false);
    }

    function stop() {
      if (frameRequest) cancelAnimationFrame(frameRequest);
      frameRequest = null;
      running = false;
      lastFrameTime = 0;
    }

    function resetAndEmit() {
      const snapshot = motion.reset();
      emit(snapshot);
    }

    function tick(now) {
      const elapsedSeconds = Math.min(
        Math.max(
          (now - (lastFrameTime || now - 1000 / 60)) / 1000,
          1 / 240
        ),
        maximumFrameSeconds
      );
      lastFrameTime = now;

      const previousX = currentX;
      const previousY = currentY;

      if (motionQuery.matches) {
        currentX = targetX;
        currentY = targetY;
      } else {
        const followBlend = 1 - Math.exp(-followRate * elapsedSeconds);
        currentX += (targetX - currentX) * followBlend;
        currentY += (targetY - currentY) * followBlend;
      }

      applyTransform(currentX, currentY);

      const deltaX = targetX - currentX;
      const deltaY = targetY - currentY;
      const distance = Math.hypot(deltaX, deltaY);
      const result = motion.update({
        elapsedSeconds: elapsedSeconds,
        movedX: currentX - previousX,
        movedY: currentY - previousY,
        targetDistance: distance,
        pointerIsIdle: now - lastTargetMoveTime > 90
      });

      emit(result.snapshot);

      if (distance < 0.25) {
        applyTransform(targetX, targetY);
        currentX = targetX;
        currentY = targetY;
      }

      if (distance < 0.25 && result.settled) {
        resetAndEmit();
        stop();
        return;
      }

      frameRequest = requestAnimationFrame(tick);
    }

    function start() {
      if (running) return;
      running = true;
      lastFrameTime = performance.now();
      frameRequest = requestAnimationFrame(tick);
    }

    function deactivate() {
      hide();
      stop();
      resetAndEmit();
    }

    function updateRect() {
      rect = boundary.getBoundingClientRect();
    }

    function sync(optionsForSync) {
      const syncSettings = optionsForSync || {};

      if (!isActive()) {
        deactivate();
        return;
      }

      const pointer = pointerProvider();
      if (!pointer) return;

      updateRect();
      const inside =
        pointer.x >= rect.left &&
        pointer.x <= rect.right &&
        pointer.y >= rect.top &&
        pointer.y <= rect.bottom;

      if (!inside) {
        deactivate();
        return;
      }

      const x = clamp(pointer.x - rect.left, 0, rect.width);
      const y = clamp(pointer.y - rect.top, 0, rect.height);
      setTarget(x, y, performance.now());

      if (!shown || syncSettings.forceJumpToCursor) {
        currentX = targetX;
        currentY = targetY;
        motion.reset();
        applyTransform(currentX, currentY);
      }

      show();
      emit(motion.getSnapshot());
      start();
    }

    function handleMouseEnter(event) {
      if (!isActive()) {
        deactivate();
        return;
      }
      updateRect();
      const x = clamp(event.clientX - rect.left, 0, rect.width);
      const y = clamp(event.clientY - rect.top, 0, rect.height);
      setTarget(x, y, performance.now());
      currentX = x;
      currentY = y;
      motion.reset();
      applyTransform(currentX, currentY);
      show();
      emit(motion.getSnapshot());
    }

    function handleMouseMove(event) {
      if (!isActive()) return;
      if (!rect) updateRect();
      setTarget(
        clamp(event.clientX - rect.left, 0, rect.width),
        clamp(event.clientY - rect.top, 0, rect.height),
        performance.now()
      );
      show();
      start();
    }

    function handleMouseLeave() {
      deactivate();
    }

    function handleVisibilityChange() {
      if (document.hidden) deactivate();
    }

    function handleReducedMotionChange() {
      if (!shown) return;
      resetAndEmit();
      sync({ forceJumpToCursor: true });
    }

    function destroy() {
      if (destroyed) return;
      destroyed = true;
      deactivate();
      boundary.removeEventListener("mouseenter", handleMouseEnter);
      boundary.removeEventListener("mousemove", handleMouseMove);
      boundary.removeEventListener("mouseleave", handleMouseLeave);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (typeof motionQuery.removeEventListener === "function") {
        motionQuery.removeEventListener("change", handleReducedMotionChange);
      }
      motion.destroy();
      [
        ["opacity", originalHostStyle.opacity],
        ["pointer-events", originalHostStyle.pointerEvents],
        ["transform", originalHostStyle.transform]
      ].forEach(function (entry) {
        if (entry[1]) host.style.setProperty(entry[0], entry[1]);
        else host.style.removeProperty(entry[0]);
      });
      if (followRegistry.get(host) === controller) followRegistry.delete(host);
      activeFollowers.delete(controller);
    }

    boundary.addEventListener("mouseenter", handleMouseEnter);
    boundary.addEventListener("mousemove", handleMouseMove);
    boundary.addEventListener("mouseleave", handleMouseLeave);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    if (typeof motionQuery.addEventListener === "function") {
      motionQuery.addEventListener("change", handleReducedMotionChange);
    }

    controller = {
      host: host,
      sync: sync,
      deactivate: deactivate,
      destroy: destroy,
      motion: motion
    };
    followRegistry.set(host, controller);
    activeFollowers.add(controller);
    return controller;
  }

  function destroyWithin(scope) {
    activeFollowers.forEach(function (controller) {
      const host = controller.host;
      if (host && (host === scope || scope?.contains?.(host))) controller.destroy();
    });
  }

  global.LTLiquidGlass = Object.freeze({
    version: VERSION,
    profile: "explore-follow-v1",
    createMotion: createMotion,
    createFollow: createPointerFollower,
    createPointerFollower: createPointerFollower,
    getLastPointer: getLastPointer,
    get: function (host) {
      return followRegistry.get(host) || null;
    },
    destroy: function (host) {
      followRegistry.get(host)?.destroy();
    },
    destroyWithin: destroyWithin,
    properties: PROPERTY_NAMES.slice()
  });
})(window);